import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, ShieldCheckIcon, TargetIcon, SettingsIcon, FileTextIcon, MapPinIcon, PenToolIcon, UserIcon, BuildingIcon, BriefcaseIcon, ShoppingBagIcon, HeartPulseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function SeoVoorWebsitesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'SEO voor websites | Hoger scoren in Google | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie. Noord Digital helpt ondernemers in Noord-Holland met duurzame vindbaarheid.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'SEO voor websites | Hoger scoren in Google | Noord',
      'og:description': 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/seo-voor-websites',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het voordat SEO resultaat oplevert?',
    a: 'SEO is een langetermijnstrategie. De eerste verbeteringen zie je vaak binnen 3 maanden, maar de grootste resultaten komen na 6 tot 12 maanden consistent werken aan je vindbaarheid.'
  }, {
    q: 'Is SEO beter dan Google Ads?',
    a: 'Beide hebben hun plek. Google Ads levert direct verkeer maar stopt zodra je stopt met betalen. SEO bouwt duurzaam organisch verkeer op dat blijft groeien. Wij adviseren vaak een combinatie.'
  }, {
    q: 'Wat kost SEO optimalisatie?',
    a: 'De kosten hangen af van de huidige staat van je website, je concurrentie en je doelen. Wij bieden transparante SEO-pakketten en bespreken vooraf wat realistisch is voor jouw budget.'
  }, {
    q: 'Kan ik zelf aan SEO werken?',
    a: 'Basis SEO kun je zelf doen, maar voor technische optimalisatie, keyword strategie en structurele verbeteringen is specialistische kennis nodig. Wij combineren beide: wij doen het zware werk, jij leert de basics.'
  }, {
    q: 'Werkt SEO ook voor lokale bedrijven?',
    a: 'Juist voor lokale bedrijven is SEO enorm waardevol. Met lokale SEO zorg je dat klanten in jouw regio (Alkmaar, Noord-Holland) jou vinden wanneer ze zoeken naar jouw diensten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1886">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1887">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1888">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1889" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1890" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1891" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1892">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1893">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1894">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1895">/</span>
            <span className="text-accent-light font-medium" data-id="element-1896">
              SEO voor websites
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1897">
            SEO voor websites — hoger scoren in Google
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1898">
            Meer organisch verkeer, betere vindbaarheid en meer klanten. Wij
            optimaliseren jouw website voor zoekmachines.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1899">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1900">
              Plan een SEO-gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-1901" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom SEO belangrijk is */}
      <section className="py-24 bg-surface-muted" data-id="element-1902">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1903">
          <div className="text-center mb-16" data-id="element-1904">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1905">
              Waarom SEO belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1906">
              Zoekmachine optimalisatie is de meest duurzame manier om online te
              groeien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1907">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1908">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1909">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-1910" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1911">
                Meer organisch verkeer
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1912">
                Gratis bezoekers via Google die actief zoeken naar jouw diensten
                of producten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1913">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1914">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-1915" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1916">
                Hogere Google posities
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1917">
                Verschijn bovenaan de zoekresultaten voor relevante zoekwoorden
                in jouw branche.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1918">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1919">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-1920" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1921">
                Meer vertrouwen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1922">
                Hoge Google posities stralen autoriteit en betrouwbaarheid uit
                naar potentiële klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1923">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1924">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-1925" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1926">
                Duurzaam resultaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1927">
                SEO levert langdurig resultaat op, ook als je stopt met
                investeren in advertenties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze SEO aanpak */}
      <section className="py-24 bg-white" data-id="element-1928">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1929">
          <div className="text-center mb-16" data-id="element-1930">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1931">
              Onze SEO aanpak
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1932">
              Een bewezen strategie voor betere vindbaarheid en meer conversie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1933">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1934">
              <div className="flex items-start gap-4" data-id="element-1935">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1936">
                  <SettingsIcon className="w-6 h-6 text-navy-700" data-id="element-1937" />
                </div>
                <div data-id="element-1938">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1939">
                    Technische SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1940">
                    Snelle laadtijden, mobiel-vriendelijk, schone code, XML
                    sitemaps en gestructureerde data voor een perfecte basis.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1941">
              <div className="flex items-start gap-4" data-id="element-1942">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1943">
                  <FileTextIcon className="w-6 h-6 text-navy-700" data-id="element-1944" />
                </div>
                <div data-id="element-1945">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1946">
                    On-page SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1947">
                    Keyword research, meta-tags, koppen structuur, interne
                    linking en content optimalisatie voor maximale relevantie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1948">
              <div className="flex items-start gap-4" data-id="element-1949">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1950">
                  <MapPinIcon className="w-6 h-6 text-navy-700" data-id="element-1951" />
                </div>
                <div data-id="element-1952">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1953">
                    Lokale SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1954">
                    Google Mijn Bedrijf, lokale zoekwoorden, NAW-consistentie en
                    reviews strategie om lokaal te domineren.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1955">
              <div className="flex items-start gap-4" data-id="element-1956">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1957">
                  <PenToolIcon className="w-6 h-6 text-navy-700" data-id="element-1958" />
                </div>
                <div data-id="element-1959">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1960">
                    Content strategie
                  </h3>
                  <p className="text-slate-600" data-id="element-1961">
                    Kennisbank artikelen, blogs en landingspagina's die ranken
                    op relevante zoektermen en bezoekers overtuigen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO resultaten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1962">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1963" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1964">
          <div className="text-center mb-16" data-id="element-1965">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1966">
              SEO resultaten die je mag verwachten
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1967">
              Meetbare groei voor jouw bedrijf door doelgerichte optimalisatie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1968">
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1969">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1970">
                Top 10 posities
              </div>
              <p className="text-navy-200" data-id="element-1971">
                Voor relevante zoekwoorden in jouw regio
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1972">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1973">
                +200% organisch verkeer
              </div>
              <p className="text-navy-200" data-id="element-1974">Gemiddelde groei binnen 6 maanden</p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1975">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1976">
                Meer leads
              </div>
              <p className="text-navy-200" data-id="element-1977">
                Bezoekers die actief op zoek zijn naar jouw dienst
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie is SEO geschikt? */}
      <section className="py-24 bg-surface-muted" data-id="element-1978">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1979">
          <div className="text-center mb-16" data-id="element-1980">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1981">
              Voor wie is SEO geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1982">
              Onze SEO-diensten zijn effectief voor diverse soorten
              ondernemingen.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-1983">
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1984">
              <MapPinIcon className="w-5 h-5 text-accent" data-id="element-1985" />
              <span className="font-medium text-navy-900" data-id="element-1986">
                Lokale ondernemers
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1987">
              <BuildingIcon className="w-5 h-5 text-accent" data-id="element-1988" />
              <span className="font-medium text-navy-900" data-id="element-1989">MKB bedrijven</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1990">
              <UserIcon className="w-5 h-5 text-accent" data-id="element-1991" />
              <span className="font-medium text-navy-900" data-id="element-1992">ZZP'ers</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1993">
              <ShoppingBagIcon className="w-5 h-5 text-accent" data-id="element-1994" />
              <span className="font-medium text-navy-900" data-id="element-1995">Webshops</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1996">
              <BriefcaseIcon className="w-5 h-5 text-accent" data-id="element-1997" />
              <span className="font-medium text-navy-900" data-id="element-1998">Dienstverleners</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1999">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2000">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2001">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2002">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2003">
              WordPress website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2004">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2005">
              Wat kost een website?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2006">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2007">
              Webdesign bureau Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2008">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2009">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2010">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2011">
          <div className="text-center mb-16" data-id="element-2012">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2013">
              Veelgestelde vragen over SEO
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2014">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2015">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2016">
                  <span className="font-bold text-navy-900" data-id="element-2017">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2018" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2019" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2020">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2021" />
    </main>;
}